import { useCallback, useState } from 'react';
import { useGameStore, useUIStore, useSettingsStore } from '@/stores';
import { SoloTeamChoice } from '@/types/game';
import './MainMenu.css';

/**
 * MainMenu - メインメニュー画面 (Dark Digital Case File デザイン)
 *
 * ゲーム世界観 (ネオン・ノワール / 警察 vs 犯人) に沿った
 * "暗号化捜査ブリーフィング端末" 風のビジュアルを採用。
 * - 黒ガラスパネル + ネオン縁 (cyan基調) + スキャンライン
 * - 警察=シアン / 犯人=マゼンタ で色分け、CTA は WANTED 赤グロー
 * - cop.png / burglar.png をマグショット風に配置
 * - 既存ラジオ <input> を維持しキーボード操作・スクリーンリーダーを保つ
 *
 * 機能 (チーム選択 / ゲーム開始 / タイトル戻る) は変更なし。
 */
const MainMenu = () => {
  const navigateTo = useUIStore((state) => state.navigateTo);
  const initializeGame = useGameStore((state) => state.initializeGame);
  // settingsStore の player.name を反映する (デフォルトは 'Player')。
  const localPlayerName = useSettingsStore((state) => state.player.name);

  // デフォルトは Team 2（ソロ側）
  const [selectedTeam, setSelectedTeam] = useState<SoloTeamChoice>('team2');

  /**
   * ゲーム開始ハンドラ
   * SoloGameSetup と同等のロジックでソロモードを初期化する
   */
  const handleStartGame = useCallback(() => {
    const playerNames = selectedTeam === 'team1'
      ? [localPlayerName, 'AI パートナー', 'AI 対戦相手']
      : [localPlayerName, 'AI プレイヤー 1', 'AI プレイヤー 2'];

    initializeGame('solo', playerNames, '1', selectedTeam);
    navigateTo('game_board');
  }, [localPlayerName, selectedTeam, initializeGame, navigateTo]);

  /**
   * ローカル対戦 (3人ホットシート) のセットアップへ
   * ソロ設定とは独立した別フロー。
   */
  const handleStartLocal = useCallback(() => {
    navigateTo('local_setup');
  }, [navigateTo]);

  /**
   * タイトル画面に戻る
   */
  const handleBackToTitle = useCallback(() => {
    navigateTo('title');
  }, [navigateTo]);

  return (
    <div className="MainMenu">
      <div className="MainMenu__paper" role="document">
        {/* 上部: 装飾テープ (純粋に装飾) */}
        <div className="MainMenu__tape" aria-hidden="true" />

        {/* ファイルヘッダー: 事件番号と分類 */}
        <header className="MainMenu__file-header" aria-hidden="true">
          <span className="MainMenu__file-meta">CASE FILE No. 0227</span>
          <span className="MainMenu__file-meta MainMenu__file-meta--right">
            DEPT. OF UNO ENFORCEMENT
          </span>
        </header>

        {/* タイトル */}
        <div className="MainMenu__title-block">
          <p className="MainMenu__eyebrow">— Operation Briefing —</p>
          <h1 className="MainMenu__title">
            UNO <span className="MainMenu__title-vs">2 vs 1</span>
            <span className="MainMenu__title-jp">チーム戦</span>
          </h1>
          <div className="MainMenu__rule" aria-hidden="true">
            <span />
            <span className="MainMenu__rule-dot" />
            <span />
          </div>
        </div>

        {/* チーム選択 (= 配属先) */}
        <fieldset className="MainMenu__section">
          <legend className="MainMenu__section-label">
            <span className="MainMenu__section-num">01</span>
            ASSIGNMENT — 配属先を選べ
          </legend>

          <div
            className="MainMenu__team-options"
            role="radiogroup"
            aria-label="チーム選択"
          >
            {/* TEAM 1: ポリス (デュオ) */}
            <label
              className={`MainMenu__team-option MainMenu__team-option--cop ${
                selectedTeam === 'team1' ? 'is-selected' : ''
              }`}
            >
              <input
                type="radio"
                name="team"
                value="team1"
                checked={selectedTeam === 'team1'}
                onChange={() => setSelectedTeam('team1')}
                className="MainMenu__radio-input"
              />
              <div className="MainMenu__mugshot" aria-hidden="true">
                <img
                  src="/assets/characters/cop.png"
                  alt=""
                  className="MainMenu__mugshot-img"
                />
                <div className="MainMenu__mugshot-bars" />
                <div className="MainMenu__mugshot-id">CPD-01</div>
              </div>
              <div className="MainMenu__team-info">
                <div className="MainMenu__team-tag MainMenu__team-tag--cop">
                  POLICE / DUO
                </div>
                <h2 className="MainMenu__team-name">Team 1 — デュオ側</h2>
                <p className="MainMenu__team-desc">
                  AI パートナーと連携し、たった一人の犯人を追い詰める。
                </p>
                <dl className="MainMenu__team-stats">
                  <div>
                    <dt>HP</dt>
                    <dd>60 pt × 2</dd>
                  </div>
                  <div>
                    <dt>Mode</dt>
                    <dd>協力</dd>
                  </div>
                </dl>
              </div>
              <span
                className="MainMenu__check"
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24" width="100%" height="100%">
                  <path
                    d="M4 12 L10 18 L20 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </label>

            {/* TEAM 2: 犯人 (ソロ) */}
            <label
              className={`MainMenu__team-option MainMenu__team-option--burglar ${
                selectedTeam === 'team2' ? 'is-selected' : ''
              }`}
            >
              <input
                type="radio"
                name="team"
                value="team2"
                checked={selectedTeam === 'team2'}
                onChange={() => setSelectedTeam('team2')}
                className="MainMenu__radio-input"
              />
              <div className="MainMenu__mugshot MainMenu__mugshot--wanted" aria-hidden="true">
                <img
                  src="/assets/characters/burglar.png"
                  alt=""
                  className="MainMenu__mugshot-img"
                />
                <div className="MainMenu__mugshot-bars" />
                <div className="MainMenu__mugshot-id">SUSP-99</div>
              </div>
              <div className="MainMenu__team-info">
                <div className="MainMenu__team-tag MainMenu__team-tag--burglar">
                  SUSPECT / SOLO
                </div>
                <h2 className="MainMenu__team-name">Team 2 — ソロ側</h2>
                <p className="MainMenu__team-desc">
                  たった一人で警察 2 人を出し抜き、夜の街に消える。
                </p>
                <dl className="MainMenu__team-stats">
                  <div>
                    <dt>HP</dt>
                    <dd>80 pt</dd>
                  </div>
                  <div>
                    <dt>Mode</dt>
                    <dd>単独</dd>
                  </div>
                </dl>
              </div>
              <span
                className="MainMenu__check"
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24" width="100%" height="100%">
                  <path
                    d="M4 12 L10 18 L20 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </label>
          </div>
        </fieldset>

        {/* アクション */}
        <div className="MainMenu__actions">
          {/* 主CTA: ソロ (上で選んだ配属先で vs AI) */}
          <button
            type="button"
            className="MainMenu__primary-button"
            onClick={handleStartGame}
          >
            <span className="MainMenu__primary-button-label">出動 / 出撃</span>
            <span className="MainMenu__primary-button-sub">
              SOLO vs AI · 上の配属先で開始
            </span>
          </button>

          {/* 別フロー: ローカル対戦 (3人ホットシート) — cyan 系セカンダリ */}
          <button
            type="button"
            className="MainMenu__local-button"
            onClick={handleStartLocal}
          >
            <span className="MainMenu__local-button-label">
              ローカル対戦 (3人ホットシート)
            </span>
            <span className="MainMenu__local-button-sub">
              PASS &amp; PLAY · 同じ画面を回して3人で対戦
            </span>
          </button>

          <button
            type="button"
            className="MainMenu__secondary-button"
            onClick={handleBackToTitle}
          >
            ← 表紙に戻る
          </button>
        </div>

        {/* 単一の "APPROVED" スタンプ (装飾) */}
        <div className="MainMenu__stamp" aria-hidden="true">
          <span>APPROVED</span>
          <small>EXECUTIVE BRANCH · 0227</small>
        </div>

        {/* フッター: 機密情報風 */}
        <footer className="MainMenu__file-footer" aria-hidden="true">
          <span>CLASSIFIED</span>
          <span className="MainMenu__file-footer-dots">· · ·</span>
          <span>FOR INTERNAL USE ONLY</span>
        </footer>
      </div>
    </div>
  );
};

export default MainMenu;
